import { defineStore } from 'pinia';
import { computed, MaybeRefOrGetter, ref, toValue } from 'vue';

import { PiniaStoreId } from '@/constants/pinia';
import { Connection, ConnectionCategory, ConnectionTypes } from '@/models/connections';
import { getConnections } from '@/services/api/connections';

const connectionCategories: Record<ConnectionCategory, ConnectionTypes[]> = {
  accounting: ['marketplace_quickbooks'],
  seedToSale: ['trace_biotrack'],
};

export const useIntegrationsStore = defineStore(PiniaStoreId.Integrations, () => {
  const connections = ref<Connection[]>([]);

  const getConnectionsByCategory = computed(() => (category: ConnectionCategory) => {
    const connectionCategory = connectionCategories[category];
    return connections.value?.filter((connection) => connectionCategory.includes(connection.type));
  });

  const getCompletedConnections = computed(() =>
    connections.value?.filter((connection) => connection.status === 'complete'),
  );
  const getCompletedConnectionsByType = computed(() => (connectionType: ConnectionTypes) => {
    return getCompletedConnections.value?.filter((connection) => connection.type === connectionType);
  });

  /**
   * Please opt for `useConnectionsQuery`, which also updates connections state.
   * The pinia state has additional computed properties that filter connections based on connection type and category.
   */
  async function fetchConnections({
    marketplace_company_id,
  }: {
    marketplace_company_id: MaybeRefOrGetter<number> | undefined;
  }) {
    const response = await getConnections({ marketplace_company_id: toValue(marketplace_company_id) });

    connections.value = response.results;
  }

  function updateConnection(connection: Connection) {
    const index = connections.value.findIndex((c) => c.id === connection.id);
    if (index !== -1) {
      connections.value.splice(index, 1, connection);
    } else {
      connections.value.push(connection);
    }
  }

  return {
    connections,
    getCompletedConnections,
    getCompletedConnectionsByType,
    getConnectionsByCategory,
    updateConnection,

    fetchConnections,
  };
});

export type IntegrationsState = ReturnType<typeof useIntegrationsStore>;
